import {
    Badge,
    Button,
    HStack,
    IconButton,
    Link,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import React from 'react';
import { FaCopy } from 'react-icons/fa';
import { translate, TranslationKeys } from '../../../i18n';
import { GameReplacementRequestDto } from '../../../models/game/dto/game.dto';
import { ReplacementRequestStatus, ReplacementRequestType } from '../../../models/payment/dto/replacement-request.dto';

interface Props {
    replacementRequests: GameReplacementRequestDto[];
    onGenerateLink?: () => void;
    isGeneratingLink?: boolean;
}

const statusColorMap: Record<ReplacementRequestStatus, string> = {
    [ReplacementRequestStatus.PENDING]: 'yellow',
    [ReplacementRequestStatus.FULFILLED]: 'green',
    [ReplacementRequestStatus.EXPIRED]: 'gray',
    [ReplacementRequestStatus.CANCELED]: 'red',
};

const statusLabelMap: Record<ReplacementRequestStatus, TranslationKeys> = {
    [ReplacementRequestStatus.PENDING]: 'game.replacement_status_pending',
    [ReplacementRequestStatus.FULFILLED]: 'game.replacement_status_fulfilled',
    [ReplacementRequestStatus.EXPIRED]: 'game.replacement_status_expired',
    [ReplacementRequestStatus.CANCELED]: 'game.replacement_status_canceled',
};

const typeLabelMap: Record<ReplacementRequestType, TranslationKeys> = {
    [ReplacementRequestType.FULL_CANCEL]: 'game.replacement_type_full_cancel',
    [ReplacementRequestType.PARTICIPANT_LEFT]: 'game.replacement_type_participant_left',
};

const ReplacementRequestsCard: React.FC<Props> = ({ replacementRequests, onGenerateLink, isGeneratingLink }) => {
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const toast = useToast();
    const hasPendingRequest = replacementRequests.some(rr => rr.status === ReplacementRequestStatus.PENDING);

    if (replacementRequests.length === 0) {
        return (
            <VStack spacing={3} py={4}>
                <Text color="gray.500" textAlign="center">
                    {translate('game.no_replacement_requests')}
                </Text>
                {onGenerateLink && (
                    <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={onGenerateLink}
                        isLoading={isGeneratingLink}
                    >
                        {translate('game.generate_replacement_link')}
                    </Button>
                )}
            </VStack>
        );
    }

    const copyLink = (link: string) => {
        navigator.clipboard?.writeText(link).then(() => {
            toast({
                title: translate('game.replacement_link_copied'),
                status: 'success',
                duration: 2000,
            });
        });
    };

    return (
        <VStack spacing={3} align="stretch">
        {onGenerateLink && !hasPendingRequest && (
            <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                alignSelf="flex-start"
                onClick={onGenerateLink}
                isLoading={isGeneratingLink}
            >
                {translate('game.generate_replacement_link')}
            </Button>
        )}
        <TableContainer>
            <Table variant="simple" size="sm">
                <Thead>
                    <Tr>
                        <Th borderColor={borderColor}>{translate('game.status')}</Th>
                        <Th borderColor={borderColor}>{translate('game.replacement_original_user')}</Th>
                        <Th borderColor={borderColor}>{translate('game.replacement_replaced_by')}</Th>
                        <Th borderColor={borderColor}>{translate('game.replacement_new_game')}</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {replacementRequests.map((rr) => (
                        <Tr key={rr.token}>
                            <Td borderColor={borderColor}>
                                <HStack spacing={2}>
                                    <Badge colorScheme={statusColorMap[rr.status]}>
                                        {translate(statusLabelMap[rr.status])}
                                    </Badge>
                                    <Badge variant="outline" colorScheme={rr.type === ReplacementRequestType.FULL_CANCEL ? 'red' : 'orange'}>
                                        {translate(typeLabelMap[rr.type])}
                                    </Badge>
                                </HStack>
                            </Td>
                            <Td borderColor={borderColor}>
                                <Text fontSize="sm">{rr.originalUserName ?? '-'}</Text>
                            </Td>
                            <Td borderColor={borderColor}>
                                <Text fontSize="sm">{rr.replacementUserName ?? '-'}</Text>
                            </Td>
                            <Td borderColor={borderColor}>
                                {rr.replacementGameId ? (
                                    <Link
                                        color="blue.400"
                                        fontWeight="semibold"
                                        fontSize="sm"
                                        href={`#/admin/gameDetails?game=${rr.replacementGameId}`}
                                    >
                                        #{rr.replacementGameId}
                                    </Link>
                                ) : rr.link ? (
                                    <Tooltip label={rr.link} hasArrow>
                                        <IconButton
                                            aria-label={translate('game.replacement_copy_link')}
                                            icon={<FaCopy />}
                                            size="xs"
                                            variant="outline"
                                            colorScheme="blue"
                                            onClick={() => copyLink(rr.link!)}
                                        />
                                    </Tooltip>
                                ) : (
                                    <Text fontSize="sm" color="gray.500">-</Text>
                                )}
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </TableContainer>
        </VStack>
    );
};

export default ReplacementRequestsCard;
